import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import letterRequestService from '../../../services/letterRequestService';
import { supabase } from '../../../lib/supabase';

const HRLetterApprovalPanel = () => {
  const [pendingLetters, setPendingLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signatureTitle, setSignatureTitle] = useState('HR Manager');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPendingLetters();
  }, []);

  const loadPendingLetters = async () => {
    try {
      setLoading(true);
      setError('');
      const letters = await letterRequestService?.getPendingLetterApprovals();
      setPendingLetters(letters);
    } catch (err) {
      setError(err?.message || 'Failed to load pending letters');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!signatureName?.trim()) {
      setError('Please enter your name for the signature');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      
      // Get HR manager ID from current user
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: employee } = await supabase
        ?.from('employees')
        ?.select('id')
        ?.eq('email', user?.email)
        ?.single();

      await letterRequestService?.approveLetterRequest(
        selectedLetter?.id,
        employee?.id,
        signatureName,
        signatureTitle
      );

      setSuccess('Letter approved successfully');
      setShowApprovalModal(false);
      setSelectedLetter(null);
      setSignatureName('');
      loadPendingLetters();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to approve letter');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason?.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: employee } = await supabase
        ?.from('employees')
        ?.select('id')
        ?.eq('email', user?.email)
        ?.single();

      await letterRequestService?.rejectLetterRequest(
        selectedLetter?.id,
        employee?.id,
        rejectionReason
      );

      setSuccess('Letter rejected');
      setShowApprovalModal(false);
      setSelectedLetter(null);
      setRejectionReason('');
      loadPendingLetters();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to reject letter');
    } finally {
      setProcessing(false);
    }
  };

  const purposeLabels = {
    banking: 'Banking Purposes',
    superannuation_withdrawal: 'Superannuation Withdrawal',
    staff_reference: 'Staff Reference',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Letter Approvals</h2>
          <p className="text-muted-foreground mt-1">
            Review and approve employee letter requests
          </p>
        </div>
        <div className="px-4 py-2 bg-warning/10 text-warning rounded-lg font-semibold">
          {pendingLetters?.length} Pending
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
          <div className="flex gap-2">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
            <p className="text-sm text-error">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
          <div className="flex gap-2">
            <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0" />
            <p className="text-sm text-success">{success}</p>
          </div>
        </div>
      )}

      {/* Pending Letters List */}
      {pendingLetters?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="CheckCircle2" size={48} className="text-success mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">All Caught Up!</h3>
          <p className="text-muted-foreground">No pending letter approvals at the moment.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingLetters?.map((letter) => (
            <div
              key={letter?.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-smooth"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      {letter?.employeeName}
                    </h3>
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                      {letter?.employeeNumber}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{letter?.employeePosition}</span>
                    <span>•</span>
                    <span>{letter?.employeeDepartment}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground mb-1">
                    {purposeLabels?.[letter?.letterPurpose]}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Requested {new Date(letter?.createdAt)?.toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Letter Preview */}
              <div className="bg-muted/30 border border-border rounded-lg p-4 mb-4">
                <p className="text-sm text-foreground line-clamp-4 whitespace-pre-wrap">
                  {letter?.generatedContent}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  onClick={() => {
                    setSelectedLetter(letter);
                    setShowApprovalModal(true);
                  }}
                  className="flex-1"
                >
                  Review & Approve
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedLetter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Review Letter Request</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedLetter?.employeeName} - {purposeLabels?.[selectedLetter?.letterPurpose]}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedLetter(null);
                  setError('');
                }}
                className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-smooth"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Letter Content */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-foreground mb-3">Letter Content</h3>
                <div className="bg-muted/30 border border-border rounded-lg p-6">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-foreground leading-relaxed">
                    {selectedLetter?.generatedContent}
                  </pre>
                </div>
              </div>

              {/* Approval Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Your Name (for signature) *
                  </label>
                  <input
                    type="text"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e?.target?.value)}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background text-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Your Title *
                  </label>
                  <input
                    type="text"
                    value={signatureTitle}
                    onChange={(e) => setSignatureTitle(e?.target?.value)}
                    placeholder="e.g., HR Manager"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background text-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Rejection Reason (if rejecting)
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e?.target?.value)}
                    placeholder="Provide reason for rejection..."
                    rows={3}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedLetter(null);
                  setError('');
                }}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                iconName="XCircle"
                onClick={handleReject}
                disabled={processing || !rejectionReason?.trim()}
                className="text-error border-error hover:bg-error/10"
              >
                Reject
              </Button>
              <Button
                variant="default"
                iconName="CheckCircle2"
                onClick={handleApprove}
                disabled={processing || !signatureName?.trim()}
                className="flex-1"
              >
                {processing ? 'Processing...' : 'Approve & Sign'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRLetterApprovalPanel;